import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiService } from "../../services/api";
import checkPermission from "../../utils/permission";
import { useModal } from "../../hooks/useModal";
import DepartmentSelector from "../../components/selectors/DepartmentSelector";
import { Bold, Italic, List, ListOrdered, Link, Code, Image, Eye } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import remarkBreaks from "remark-breaks";

export default function EditAnnouncement() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showAlert } = useModal();

  useEffect(() => {
    if (!checkPermission("announcement_update")) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    display: false,
  });
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
  const [selectedDepartmentDetails, setSelectedDepartmentDetails] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const fieldClassName =
    "w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-50";
  const sectionClassName = "rounded-xl border border-slate-200 bg-white p-4 sm:p-5";
  const sectionBadgeClassName =
    "inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700";

  useEffect(() => {
    const fetchAnnouncement = async () => {
      if (!id) {
        navigate("/announcements", { replace: true });
        return;
      }

      try {
        setInitialLoading(true);
        const announcement = await ApiService.getAnnouncement(id);

        setFormData({
          title: announcement.title || "",
          message: announcement.message || "",
          display: announcement.display || false,
        });

        // Çoklu departman verilerini yükle (departments array'i)
        if (announcement.departments && announcement.departments.length > 0) {
          const deptIds = announcement.departments.map((d: any) => d.id);
          const deptDetails: Record<string, any> = {};
          announcement.departments.forEach((dept: any) => {
            deptDetails[dept.id] = dept;
          });
          setSelectedDepartmentIds(deptIds);
          setSelectedDepartmentDetails(deptDetails);
        }
        // Eski tek departman verisi varsa onu da ekle
        else if (announcement.department) {
          setSelectedDepartmentIds([announcement.department.id]);
          setSelectedDepartmentDetails({
            [announcement.department.id]: announcement.department,
          });
        }
      } catch (error: any) {
        console.error("Duyuru yükleme hatası:", error);
        await showAlert({
          title: "Duyuru Bulunamadı",
          message: "Aradığınız duyuru bulunamadı veya silinmiş olabilir.",
          type: "error",
        });
        navigate("/announcements", { replace: true });
      } finally {
        setInitialLoading(false);
      }
    };

    fetchAnnouncement();
  }, [id, navigate, showAlert]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const insertMarkdown = (prefix: string, suffix: string = "", placeholder: string = "") => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.message.substring(start, end);
    const textToInsert = selectedText || placeholder;
    const newText = formData.message.substring(0, start) + prefix + textToInsert + suffix + formData.message.substring(end);

    setFormData((prev) => ({ ...prev, message: newText }));

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + prefix.length + textToInsert.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Duyuru başlığı gereklidir";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Duyuru mesajı gereklidir";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id) return;

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await ApiService.updateAnnouncement(id, {
        title: formData.title.trim(),
        message: formData.message.trim(),
        display: formData.display,
        departmentIds: selectedDepartmentIds,
      });

      await showAlert({
        title: "Başarılı",
        message: "Duyuru güncellendi!",
        type: "success",
      });

      navigate("/announcements", { replace: true });
    } catch (error: any) {
      console.error("Duyuru güncelleme hatası:", error);
      await showAlert({
        title: "Hata",
        message: error.message || "Duyuru güncellenemedi.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="h-[88vh] bg-gray-100 overflow-hidden flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-[88vh] bg-gray-100 overflow-hidden flex flex-col">
      <nav className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">Duyurular</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <button
                onClick={() => navigate("/announcements")}
                className="pb-3 transition-all duration-200 text-gray-900 font-semibold border-b-2 border-gray-900">
                Tüm Duyurular
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-5 sm:p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Duyuruyu Düzenle</h1>
                <p className="text-sm text-gray-600 mt-1">Duyuru bilgilerini güncelleyin ve düzenleyin.</p>
                <div className="mt-3 h-1 w-20 rounded-full bg-blue-200" />
              </div>
              <div className="text-right">
                {formData.title && (
                  <div>
                    <p className="text-gray-900 font-semibold text-base sm:text-lg">{formData.title}</p>
                    <p className="text-gray-600 text-sm">Duyuru Düzenleniyor</p>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => navigate("/announcements")}
                  className="mt-2 inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50">
                  Geri Dön
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className={sectionClassName}>
                <div className="mb-3">
                  <span className={sectionBadgeClassName}>
                    Temel Bilgiler
                  </span>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duyuru Başlığı <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className={`${fieldClassName} ${errors.title ? "border-red-500 focus:border-red-300" : ""}`}
                      placeholder="Duyuru başlığını buraya yazın..."
                    />
                    {errors.title && <p className="mt-1.5 text-xs text-red-600">{errors.title}</p>}
                  </div>
                </div>
              </div>

              <div className={sectionClassName}>
                <div className="mb-3">
                  <span className={sectionBadgeClassName}>
                    Duyuru İçeriği
                  </span>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Duyuru Mesajı <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowPreview(!showPreview)}
                      className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">
                      <Eye size={14} />
                      {showPreview ? "Düzenle" : "Önizleme"}
                    </button>
                  </div>

                  {!showPreview && (
                    <>
                      <div className="flex flex-wrap gap-1 mb-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                        <button
                          type="button"
                          onClick={() => insertMarkdown("**", "**", "kalın yazı")}
                          className="p-1.5 hover:bg-white rounded text-gray-600 hover:text-gray-900 transition-colors border border-transparent hover:border-gray-300"
                          title="Kalın (Bold)">
                          <Bold size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => insertMarkdown("*", "*", "italik yazı")}
                          className="p-1.5 hover:bg-white rounded text-gray-600 hover:text-gray-900 transition-colors border border-transparent hover:border-gray-300"
                          title="İtalik (Italic)">
                          <Italic size={16} />
                        </button>
                        <div className="w-px bg-gray-300 mx-1"></div>
                        <button
                          type="button"
                          onClick={() => insertMarkdown("\n- ", "", "liste öğesi")}
                          className="p-1.5 hover:bg-white rounded text-gray-600 hover:text-gray-900 transition-colors border border-transparent hover:border-gray-300"
                          title="Madde İşaretli Liste">
                          <List size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => insertMarkdown("\n1. ", "", "liste öğesi")}
                          className="p-1.5 hover:bg-white rounded text-gray-600 hover:text-gray-900 transition-colors border border-transparent hover:border-gray-300"
                          title="Numaralı Liste">
                          <ListOrdered size={16} />
                        </button>
                        <div className="w-px bg-gray-300 mx-1"></div>
                        <button
                          type="button"
                          onClick={() => insertMarkdown("[", "](url)", "link metni")}
                          className="p-1.5 hover:bg-white rounded text-gray-600 hover:text-gray-900 transition-colors border border-transparent hover:border-gray-300"
                          title="Link Ekle">
                          <Link size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => insertMarkdown("`", "`", "kod")}
                          className="p-1.5 hover:bg-white rounded text-gray-600 hover:text-gray-900 transition-colors border border-transparent hover:border-gray-300"
                          title="Kod (Inline)">
                          <Code size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => insertMarkdown("![", "](url)", "resim açıklaması")}
                          className="p-1.5 hover:bg-white rounded text-gray-600 hover:text-gray-900 transition-colors border border-transparent hover:border-gray-300"
                          title="Resim Ekle">
                          <Image size={16} />
                        </button>
                      </div>

                      <textarea
                        ref={textareaRef}
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={8}
                        className={`${fieldClassName} resize-none ${errors.message ? "border-red-500 focus:border-red-300" : ""}`}
                        placeholder="Duyuru mesajını buraya yazın... (Markdown destekli)"
                      />
                    </>
                  )}

                  {showPreview && (
                    <div className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 min-h-[200px] prose prose-sm max-w-none overflow-hidden">
                      {formData.message ? (
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm, remarkBreaks]}
                          rehypePlugins={[rehypeHighlight]}
                          components={{
                            a: ({ children, href }) => (
                              <a
                                href={href}
                                className="text-blue-600 hover:underline font-medium"
                                style={{
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                }}
                                target="_blank"
                                rel="noopener noreferrer">
                                {children}
                              </a>
                            ),
                            p: ({ children }) => <p className="mb-2 whitespace-pre-wrap">{children}</p>,
                            ul: ({ children }) => <ul className="list-disc ml-6 mb-2">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal ml-6 mb-2">{children}</ol>,
                            li: ({ children }) => <li className="mb-1 whitespace-pre-wrap">{children}</li>,
                            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                            em: ({ children }) => <em className="italic">{children}</em>,
                          }}>
                          {formData.message}
                        </ReactMarkdown>
                      ) : (
                        <p className="text-gray-400 text-sm italic">Önizlemek için mesaj yazın...</p>
                      )}
                    </div>
                  )}

                  {errors.message && <p className="mt-1.5 text-xs text-red-600">{errors.message}</p>}
                  <p className="mt-3 text-xs text-slate-500">
                    <strong>Markdown kullanabilirsiniz:</strong> **kalın**, *italik*, [link](url), `kod`, listeler vb.
                  </p>
                </div>
              </div>

              <div className={sectionClassName}>
                <div className="mb-3">
                  <span className={sectionBadgeClassName}>
                    Hedef Kitle
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Departman Seçimi</label>

                  <div className="mb-4">
                    <DepartmentSelector
                      value=""
                      onChange={(departmentId, department) => {
                        if (departmentId && department) {
                          if (!selectedDepartmentIds.includes(departmentId)) {
                            setSelectedDepartmentIds((prev) => [...prev, departmentId]);
                            setSelectedDepartmentDetails((prev) => ({
                              ...prev,
                              [departmentId]: department,
                            }));
                          }
                        }
                      }}
                      placeholder="Departman Seçin"
                      excludeIds={selectedDepartmentIds}
                      allowClear={false}
                      showColors={true}
                      className="w-full rounded-xl"
                    />
                  </div>

                  {selectedDepartmentIds.length > 0 && (
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                      <div className="text-sm font-medium text-blue-900 mb-2">Seçili Departmanlar ({selectedDepartmentIds.length})</div>
                      <div className="flex flex-wrap gap-1">
                        {selectedDepartmentIds.map((deptId) => {
                          const dept = selectedDepartmentDetails[deptId];
                          return dept ? (
                            <span
                              key={deptId}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                              {dept.color && <div className="w-3 h-3 rounded-full" style={{ backgroundColor: dept.color }} />}
                              {dept.name}
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedDepartmentIds((prev) => prev.filter((id) => id !== deptId));
                                  setSelectedDepartmentDetails((prev) => {
                                    const newDetails = { ...prev };
                                    delete newDetails[deptId];
                                    return newDetails;
                                  });
                                }}
                                className="ml-1 text-blue-600 hover:text-blue-800">
                                ×
                              </button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}

                  <p className="mt-3 text-xs text-slate-500">
                    Hiçbir departman seçilmezse duyuru tüm kullanıcılara gösterilir. Departman seçilirse sadece o departmanlardaki
                    kullanıcılar görebilir.
                  </p>
                </div>
              </div>

              <div className={sectionClassName}>
                <div className="mb-3">
                  <span className={sectionBadgeClassName}>
                    Görünürlük Ayarları
                  </span>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <span className="text-sm font-semibold text-gray-900 block">Ana Sayfada Görünür</span>
                      <span className="text-xs text-gray-600 mt-1 block">
                        Yalnızca en son oluşturulan "Ana Sayfada Görünür" durumundaki duyuru ana sayfada gösterilecektir.
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, display: !prev.display }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${formData.display ? "bg-blue-600" : "bg-gray-200"
                        }`}>
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.display ? "translate-x-6" : "translate-x-1"
                          }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 z-10 -mx-2 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur">
                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => navigate("/announcements")}
                    className="px-4 py-2 bg-white text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 text-sm">
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={!formData.title.trim() || !formData.message.trim() || loading}
                    className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-md hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60 text-sm">
                    {loading ? "Güncelleniyor..." : "Duyuru Güncelle"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
